import { Button, Icon, Spacing, TextInput } from 'zens-storybook'
import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { AccessType, StorageKey, Workspace, WorkspacesFilter, useStore } from 'zens-store'
import { useRouter, useNumberParams, Link, useFilterParam } from 'zens-router'
import { useBreadcrumb, usePermissions } from '@dms/hook'

import { WorkspaceFloorplanSelect, WorkspaceListHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    margin: Spacing.L,
  },
  header: {
    flexDirection: 'row',
  },
  search: {
    minWidth: 300,
    marginRight: Spacing.S,
  },
})

const ScreenWorkspaceFloorplanSelect = () => {
  const router = useRouter()
  const [{ companyId, floorId }, setNumberParam] = useNumberParams()
  const { t } = useTranslation()
  const { storage } = useStore()
  const data = storage.get(`${companyId}:${StorageKey.WorkspaceFilter}`, { session: true })
  const defaultValue = data ? JSON.parse(data) : undefined
  const [filter, setFilter] = useFilterParam<WorkspacesFilter>(defaultValue)
  const { hasPermission } = usePermissions(companyId)

  const breadcrumb = useBreadcrumb([
    {
      title: t('Workspaces'),
    },
  ])

  function updateFloor(floorId: number) {
    setNumberParam({ floorId })
  }

  function updateSearch(search: string) {
    setFilter({ search })
  }

  const show = (workspace: Workspace) => {
    router.push(`/companies/${companyId}/workspaces/${workspace.id}`)
  }

  const renderHeader = () => {
    return (
      <View style={styles.header}>
        <TextInput
          left="search"
          placeholder={t('Search workspace name')}
          right={
            filter?.search ? (
              <TouchableOpacity onPress={() => updateSearch('')} testID="WorkspaceFloorplanSelect.Search.Clear">
                <Icon name="close" size="S" />
              </TouchableOpacity>
            ) : undefined
          }
          value={filter?.search || ''}
          onChangeText={updateSearch}
          style={styles.search}
          testID="WorkspaceFloorplanSelect.Search"
        />
        {hasPermission(AccessType.Update, 'workspace') &&
          hasPermission(AccessType.Create, 'workspace') && (
            <Link href={`/companies/${companyId}/workspaces/add`} asChild>
              <Button title={t('Add workspace')} left="plus" testID="WorkspaceFloorplanSelect.Add" />
            </Link>
          )}
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<WorkspaceListHeader.Skeleton />}>
        <WorkspaceListHeader breadcrumb={breadcrumb} companyId={companyId} filter={filter} accessory={renderHeader()} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<WorkspaceFloorplanSelect.Skeleton />}>
          <WorkspaceFloorplanSelect
            companyId={companyId}
            search={filter?.search}
            onSelect={hasPermission(AccessType.Read, 'workspaceDetail') ? show : undefined}
            floorId={floorId}
            onChangeFloor={updateFloor}
            typeHref={(type) =>
              type === 'table'
                ? {
                    pathname: `/companies/${companyId}/workspaces`,
                    params: { filter: JSON.stringify(filter) },
                  }
                : {
                    pathname: `/companies/${companyId}/workspaces/floorplan`,
                    params: { filter: JSON.stringify(filter), floorId },
                  }
            }
          />
        </Suspense>
      </View>
    </View>
  )
}

export default ScreenWorkspaceFloorplanSelect
